import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router-dom';
import { Box, Flex } from '@chakra-ui/react';

import BackButton from '../../components/BackButton';
import Page from '../../components/Page';
import { buildQuickFilterOptions } from '../../components/search/util';
import LoadingSpinner from '../../components/Spinner';
import { analyticsIds } from '../../constants/analyticsIds';
import { JobProfilePage } from '../../constants/duskIds';
import { Statuses } from '../../constants/statuses';
import { setCurrentPage } from '../../redux/modules/app';
import { getCompanyDepartments, getCompanyUsers, selectCompanyDepartments, selectCompanyUsers } from '../../redux/modules/company';
import { clearDepartment, getDepartmentJobs, selectDepartmentJobs } from '../../redux/modules/department';
import { getCurrentJob, getJobProfile, resetJobs, selectCurrentJob, selectJobProfile } from '../../redux/modules/jobs';
import { jobSearch } from '../../redux/modules/jobSearch';
import { globalRequirementTemplateBuilder, jobDetailsSearch } from '../../redux/reducer';
import { getContentHeightString } from '../../styles/layoutDimensions';
import { sortAlphaNumeric } from '../../utils/arrayUtil';

import JobInfoPanel from './components/JobInfoPanel';
import JobInsuredsPanelHeader from './components/JobInsuredsPanelHeader';
import SearchPanel from './components/SearchPanel';
import quickFilters from './quickFilters';
import { statusOptions } from './util';

const JobDetailsPage = () => {
  const { companyId, jobId } = useParams();
  const { url } = useRouteMatch();
  const dispatch = useDispatch();
  const departmentJobs = useSelector(selectDepartmentJobs);
  const users = useSelector(selectCompanyUsers);
  const departments = useSelector(selectCompanyDepartments);
  const templatesLoading = useSelector(globalRequirementTemplateBuilder.selectAllPublishedTemplatesLoading);
  const job = useSelector(selectCurrentJob);
  const jobProfile = useSelector(selectJobProfile);

  useEffect(() => {
    dispatch(jobDetailsSearch.clearSearch());
    dispatch(setCurrentPage('Job Details'));
    dispatch(globalRequirementTemplateBuilder.listAllPublishedTemplates());
    dispatch(getCurrentJob(jobId));
    dispatch(getCompanyDepartments(companyId));
    dispatch(getCompanyUsers(companyId));

    dispatch(jobDetailsSearch.setColumns([
      { id: 'insured', accessor: 'insured.company_name.keyword', sortKey: 'insured.company_name.keyword', sortName: 'insuredName' },
      { id: 'status', accessor: 'status', sortKey: 'status.keyword', sortName: 'status' },
      {
        id: 'record_number',
        accessor: 'insurance_requirement.contract_number',
        sortKey: 'insurance_requirement.contract_number.keyword',
        sortName: 'recordNumber'
      }
    ]));

    return () => {
      dispatch(resetJobs());
      dispatch(clearDepartment());
      dispatch(jobDetailsSearch.clearSearch());
    };
  }, [companyId, dispatch, jobId]);

  useEffect(() => {
    if (users && departments && !templatesLoading) {
      dispatch(getJobProfile(jobId));
    }
  }, [departments, dispatch, jobId, users, templatesLoading]);

  useEffect(() => {
    if (job && job?.stats && !departmentJobs?.length) {
      dispatch(getDepartmentJobs(job.departmentId));
    }
  }, [dispatch, job, jobId, departmentJobs]);

  useEffect(() => {
    if (departmentJobs && jobId && job?.stats) {
      dispatch(jobDetailsSearch.setQuickFilterOptions(buildQuickFilterOptions(quickFilters, {
        jobId: departmentJobs.filter(j => j.jobId === jobId),
        jobName: departmentJobs.sort(sortAlphaNumeric('jobName'))
      })));

      dispatch(jobDetailsSearch.setBaseFilters([
        { key: 'job.id', value: jobId }
      ]));

      if (jobProfile.status === Statuses.Archived) {
        dispatch(jobDetailsSearch.setBaseFilters([
          { key: 'job.id', value: jobId },
          { key: 'status.keyword', value: 'archived' }
        ]));
      } else {
        dispatch(jobDetailsSearch.setBaseFilters([
          { key: 'job.id', value: jobId },
          { key: 'status.keyword', value: ['approved', 'not_approved'] }
        ]));
      }

      dispatch(jobDetailsSearch.getPage());
    }
    }, [departmentJobs, dispatch, jobId]); // eslint-disable-line

  return (
    <Page fixedHeight backgroundColor="grey" dusk={JobProfilePage.jobProfilePageId}>
      <Box height="inherit">
        <Box height="32px" mb={5}>
          <BackButton
            dataAnalyticsId={analyticsIds.projectDetailsPage.backToProjectListButton}
            executable={() => dispatch(jobSearch.getPage())}
            text="Projects"
            url={url.replace(/projects.*/, 'projects')}
          />
        </Box>
        <Flex h={`calc(${getContentHeightString()} - 32px - 1.25rem)`} justify="space-between" w="100%">
          {job && jobProfile ? (
            <>
              <JobInfoPanel />
              <Box h="inherit" w="100%">
                <JobInsuredsPanelHeader />
                <SearchPanel
                  searchBlueprint={jobDetailsSearch}
                  viewOnly={jobProfile.status === Statuses.Archived}
                />
              </Box>
            </>
          ) : <LoadingSpinner />}
        </Flex>
      </Box>
    </Page>
  );
};

export default JobDetailsPage;
